// Paying needs a member account. Signed-out visitors and anonymous-trial guests
// register first and then continue where they were headed: straight away for a
// guest (its account upgrades in place), after email verification and sign-in
// for a new visitor.
import { safeAuthNext } from "./safePath";

const PENDING_KEY = "pendingAuthNext";
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

export function isMember(store) {
  const user = store.getters.currentUser;
  return !!(store.getters.isAuthenticated && user && !user.is_anonymous);
}

// A router location for `next`, carrying the picked plan for the plan pages.
function continuation(next, plan) {
  const path = safeAuthNext(next);
  if (!path) return null;
  const target = { path };
  if (typeof plan === "string" && plan)
    target.query = { plan: plan.toLowerCase() };
  return target;
}

export function registerFirst(next, plan) {
  const target = continuation(next, plan);
  if (!target) return { path: "/register" };
  return {
    path: "/register",
    query: { next: target.path, ...(target.query || {}) }
  };
}

// What a sign-in or register page passes on to its sibling page.
export function authQuery(query) {
  const target = continuation(query && query.next, query && query.plan);
  if (!target) return {};
  return { next: target.path, ...(target.query || {}) };
}

// A new visitor verifies their email (often in another tab) before signing in,
// so the destination waits in this browser until that sign-in.
export function rememberAuthNext(query) {
  const target = continuation(query && query.next, query && query.plan);
  if (!target) return false;
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({
        next: target.path,
        plan: target.query ? target.query.plan : null,
        at: Date.now()
      })
    );
    return true;
  } catch (error) {
    return false; // Storage unavailable: the user lands on the home page instead.
  }
}

// Where to go after signing in or registering: an explicit ?next= first, else
// a destination remembered by a registration in this browser. Always consumed.
export function takeAuthNext(query) {
  let pending = null;
  try {
    pending = JSON.parse(localStorage.getItem(PENDING_KEY) || "null");
    localStorage.removeItem(PENDING_KEY);
  } catch (error) {
    pending = null;
  }
  const explicit = continuation(query && query.next, query && query.plan);
  if (explicit) return explicit;
  if (
    pending &&
    typeof pending.at === "number" &&
    Date.now() - pending.at < PENDING_TTL_MS
  ) {
    return continuation(pending.next, pending.plan);
  }
  return null;
}

// The server's own reason (for example a 403 detail) instead of a generic failure.
export function serverMessage(error, fallback) {
  const data = error && error.response ? error.response.data : null;
  const detail = data ? data.detail : null;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}
